<template>
  <import-failed-modal v-if="importFailed && requiresFullImport" />
  <loading v-else textColor="blue" :progress="overallProgress" />
</template>

<script>
import Loading from "@/components/layouts/Loading";
import ImportFailedModal from "@/components/modals/ImportFailedModal";
import { mapActions, mapGetters } from "vuex";
import { ProgressStepId } from "@/utils/ProgressStepId";
import { ProgressState } from "@/utils/ProgressState";

export default {
  name: "Update",
  components: { Loading, ImportFailedModal },
  data: () => {
    return {
      isLoading: true,
      hasUpdatedCommonData: false,
      dontShowProgress: true,
      supportsNewUserFlow: false
    };
  },
  async created() {
    this.hasUpdatedCommonData = false;
    await this.performUpdate();

    if (!this.isLoading || (this.importFailed && this.requiresFullImport)) {
      return; // the import has been interrupted
    }

    await this.$store.dispatch("storage/fetchImportProgress");

    this.isLoading = false;
    const hasCompletedOnboarding = this.hasProgress(ProgressStepId.Onboarding);
    let nextRoute = hasCompletedOnboarding
      ? { name: "government-home" }
      : { name: "splash" };
    await this.$router.push(nextRoute);
    if (!hasCompletedOnboarding) {
      await this.updateProgression({
        key: ProgressStepId.Onboarding,
        data: { state: ProgressState.Completed }
      });
    }
  },
  computed: {
    ...mapGetters("user", ["user", "hasProgress"]),
    ...mapGetters("storage", [
      "isOnlineUser",
      "importProgress",
      "importFailed",
      "requiresFullImport"
    ]),
    overallProgress() {
      if (this.dontShowProgress) {
        return 0;
      }

      /**
       * The importProgress provides progress for current api call, not the total of all api calls.
       * Updating common data is a much faster task compared to updating canton data
       * so updating common data corresponds to 10% progress.
       * */
      const ratioCommon = 0.1;
      const ratioCanton = 1 - ratioCommon;
      let progress = 0;

      if (this.hasUpdatedCommonData) {
        progress = this.importProgress * ratioCanton + 100 * ratioCommon;
      } else {
        progress = this.importProgress * ratioCommon;
      }

      return Math.trunc(progress);
    }
  },
  beforeRouteEnter(to, from, next) {
    let delay = 200;
    if (from.name === "start") {
      // delay displaying loading progress if starting up app
      delay = 1000;
    }

    next(vm => {
      vm.isLoading = true;
      vm.dontShowProgress = true;
      setTimeout(vm.fetchProgress, delay);
    });
  },
  async beforeRouteLeave(to, from, next) {
    if (this.isLoading) {
      // interrupt import
      this.isLoading = false;
      await this.$store.dispatch("storage/interruptImport");
    }

    next();
  },
  methods: {
    ...mapActions("user", ["updateProgression"]),
    async performUpdate() {
      try {
        const cantonId = this.user.cantonId;
        if (this.isLoading) {
          await this.$store.dispatch("storage/updateCommonData");
          this.hasUpdatedCommonData = true;
          await this.$store.dispatch("storage/fetchImportProgress");
        }
        if (this.isLoading) {
          await this.$store.dispatch("storage/updateCantonRelatedData", {
            cantonId
          });
        }
      } catch (err) {
        this.isLoading = false;
        this.$store.commit("storage/setFailed", true);
        // show an error message, allow force closing the app
      }
    },
    fetchProgress() {
      if (this.isLoading && !this.importFailed) {
        this.dontShowProgress = false;
        this.$store.dispatch("storage/fetchImportProgress");
        setTimeout(this.fetchProgress, 200);
      } else {
        // do nothing
      }
    }
  }
};
</script>

<style scoped></style>
